import { createFileRoute } from '@tanstack/react-router'
import CaptureRow, { type CaptureRowData } from '@/components/CaptureRow'
import useNewSlideDetector from '@/hooks/useNewSlideDetector';
import { useCallback, useReducer } from 'react';
import { uploadSlideWithRetry } from '@/helpers/uploadSlide';
/**
 * Creates a JPEG data URL from a VideoFrameMessage's frame data.
 * @param videoFrameMessage The video frame message containing image data.
 * @returns JPEG data URL string
 */
function createJpegFromVideoFrame(videoFrameMessage: VideoFrameMessage): string {
    const { frameData } = videoFrameMessage;
    const { width, height, data } = frameData;
    // Create a canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    // Put the ImageData directly
    ctx.putImageData(frameData, 0, 0);
    // Export as high quality JPEG
    return canvas.toDataURL('image/jpeg', 0.95);
}
import type { VideoFrameMessage } from '@/hooks/useVideoCapture';
import CaptureComponent from '@/components/CaptureComponent';

export const Route = createFileRoute('/capture')({
    component: CaptureDemo,
})

type RowsAction =
    | { type: 'append', row: CaptureRowData }
    | { type: 'remove', id: string };

function rowsReducer(state: CaptureRowData[], action: RowsAction): CaptureRowData[] {
    switch (action.type) {
        case 'append':
            const newRow = {...action.row, id: state.length + 1 + ''}; // assign a new id
            return [newRow, ...state]; // rows are sorted in reverse chronological order
        case 'remove':
            return state.filter(row => row.id !== action.id);
        default:
            return state;
    }
}

const SAMPLE_ROWS: CaptureRowData[] = [
        {
            id: '1',
            thumbnail: 'https://via.placeholder.com/160x90',
            time: '00:12:34',
            offset: '3:12 elapsed',
            status: 'green',
        },
        {
            id: '2',
            thumbnail: 'https://via.placeholder.com/160x90',
            time: '00:15:20',
            offset: '6:58 elapsed',
            status: 'yellow',
        },
        {
            id: '3',
            thumbnail: 'https://via.placeholder.com/160x90',
            time: '00:20:10',
            offset: '11:48 elapsed',
            status: 'orange',
        },
];




/**
 * A Page that captures new video slides and adds them to a list
 */
function CaptureDemo() {
    const autoSlideDetected = useCallback(async (videoFrameMessage: VideoFrameMessage) => {
        console.log('Auto slide detected callback', videoFrameMessage)
        const jpegOfFrame = createJpegFromVideoFrame(videoFrameMessage);
        
        // Upload the slide to the API
        try {
            // TODO: Get the actual place ID from context/props/config
            const placeId = 'demo-place-123'; // Replace with actual place ID
            
            const uploadResponse = await uploadSlideWithRetry(jpegOfFrame, placeId);
            console.log('Slide uploaded successfully:', uploadResponse);
            
            // Create new row with successful upload status
            const newRow: CaptureRowData = {
                id: '', // will be set in reducer
                thumbnail: jpegOfFrame,
                time: new Date().toLocaleTimeString(),
                offset: '0:00 elapsed', // You can calculate actual elapsed time if needed
                status: 'green', // Successfully uploaded
            };
            
            // Dispatch the new row to the reducer
            dispatchRows({ type: 'append', row: newRow });
        } catch (error) {
            console.error('Failed to upload slide:', error);
            
            // Create new row with error status
            const newRow: CaptureRowData = {
                id: '', // will be set in reducer
                thumbnail: jpegOfFrame,
                time: new Date().toLocaleTimeString(),
                offset: '0:00 elapsed', // You can calculate actual elapsed time if needed
                status: 'orange', // Upload failed
            };
            
            // Dispatch the new row to the reducer
            dispatchRows({ type: 'append', row: newRow });
        }
    }, []);
    useNewSlideDetector(autoSlideDetected);

    const handleRowAction = (action: string, rowId: string) => {
        console.log(`Action: ${action}, Row ID: ${rowId}`)
        // Add your action handling logic here
        switch (action) {
            case 'edit':
                // Handle edit action
                break
            case 'duplicate':
                // Handle duplicate action
                break
            case 'delete':
                // Handle delete action
                dispatchRows({ type: 'remove', id: rowId });
                break
            default:
                break
        }
    }



    const [rowsData, dispatchRows] = useReducer(rowsReducer, SAMPLE_ROWS);

    return (
        <main>
            <CaptureComponent />
            {rowsData.map((row) => (
                <CaptureRow key={row.id} row={row} onAction={handleRowAction} />
            ))}
        </main>
    )
}
